<template>
  <div class="page-scaffold">
    <widget-header-main
        style="grid-area: top-bar"
        :loading="loading"
        :number="resolvedHeader.number"
        :chat-badge="resolvedHeader.chatBadge"
        :notification-badge="resolvedHeader.notificationBadge"
        :chat-active="resolvedHeader.chatActive"
        @profile="handleProfile"
        @support="handleSupport"
        @notifications="handleNotifications"
        @logout="handleLogout"
        @switch-account="openAccountSwitcher"
    />
    <div class="safe-zone" style="grid-area: safe-zone-l"/>
    <slot class="content" style="grid-area: content"></slot>
    <div class="safe-zone" style="grid-area: safe-zone-r"/>
    <div v-if="isAccountSwitcherOpen" class="account-switcher">
      <div class="account-switcher__overlay" @click="closeAccountSwitcher"></div>
      <div class="account-switcher__panel" role="dialog" aria-modal="true" @click.stop>
        <div class="account-switcher__header">
          <div class="account-switcher__title">Сменить или добавить аккаунт</div>
          <button type="button" class="account-switcher__close" @click="closeAccountSwitcher">x</button>
        </div>
        <div class="account-switcher__actions">
          <button type="button" class="account-switcher__add" @click="goToAddAccount">
            Добавить аккаунт
          </button>
        </div>
        <div v-if="accounts.length" class="account-switcher__list">
          <div
            v-for="accountItem in accounts"
            :key="accountItem.id"
            class="account-switcher__item"
            :class="{ 'account-switcher__item--active': accountItem.id === activeAccountId }"
          >
            <div class="account-switcher__info">
              <div class="account-switcher__name">{{ resolveAccountName(accountItem) }}</div>
              <div class="account-switcher__meta">
                <span>Договор № {{ resolveAgreementNumber(accountItem) }}</span>
                <span v-if="resolvePersonalAccount(accountItem)">/ Лицевой счет № {{ resolvePersonalAccount(accountItem) }}</span>
              </div>
            </div>
            <div class="account-switcher__controls">
              <button
                type="button"
                class="account-switcher__btn"
                :disabled="accountItem.id === activeAccountId"
                @click="switchToAccount(accountItem.id)"
              >
                {{ accountItem.id === activeAccountId ? 'Активен' : 'Переключиться' }}
              </button>
              <button
                type="button"
                class="account-switcher__btn account-switcher__btn--ghost"
                @click="removeAccountEntry(accountItem.id)"
              >
                Выйти
              </button>
            </div>
          </div>
        </div>
        <div v-else class="account-switcher__empty">
          Пока нет добавленных аккаунтов.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WidgetHeaderMain from "./widget-header-main";
import {getAccountsSnapshot, handleAuthFailure, removeAccount, switchAccount} from "../utils/multi-account";

export default {
  name: "page-scaffold",
  components: {
    WidgetHeaderMain
  },
  emits: ['profile', 'support', 'notifications', 'logout'],
  data() {
    return {
      isAccountSwitcherOpen: false,
      accounts: [],
      activeAccountId: null
    }
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    header: {
      type: Object,
      default: () => ({
        number: '',
        chatBadge: false,
        notificationBadge: false,
        chatActive: true
      })
    }
  },
  computed: {
    resolvedHeader() {
      const defaults = {
        number: localStorage.agreementNumber || '—',
        chatBadge: localStorage.chatBadge === 'true',
        notificationBadge: localStorage.notificationBadge === 'true',
        chatActive: localStorage.chatActive !== 'false'
      }

      return {
        number: this.header.number || defaults.number,
        chatBadge: typeof this.header.chatBadge === 'boolean' ? this.header.chatBadge : defaults.chatBadge,
        notificationBadge: typeof this.header.notificationBadge === 'boolean' ? this.header.notificationBadge : defaults.notificationBadge,
        chatActive: typeof this.header.chatActive === 'boolean' ? this.header.chatActive : defaults.chatActive
      }
    }
  },
  methods: {
    toHandlerKey(eventName) {
      if (!eventName) {
        return ''
      }
      const capitalized = eventName.charAt(0).toUpperCase() + eventName.slice(1)
      return `on${capitalized}`
    },
    hasListener(eventName) {
      const instance = this.$ && this.$.vnode
      if (!instance || !instance.props) {
        return false
      }
      const handlerKey = this.toHandlerKey(eventName)
      const handler = instance.props[handlerKey] ?? instance.props[`${handlerKey}Once`]
      if (Array.isArray(handler)) {
        return handler.length > 0
      }
      return typeof handler === 'function'
    },
    emitIfListener(eventName) {
      if (this.hasListener(eventName)) {
        this.$emit(eventName)
        return true
      }
      return false
    },
    handleProfile() {
      if (this.emitIfListener('profile')) {
        return
      }
      this.$router.push('/profile')
    },
    handleSupport() {
      if (this.emitIfListener('support')) {
        return
      }
      this.$router.push('/chat')
    },
    handleNotifications() {
      if (this.emitIfListener('notifications')) {
        return
      }
      this.$router.push('/notifications')
    },
    handleLogout() {
      if (this.emitIfListener('logout')) {
        return
      }
      this.performLogout()
    },
    openAccountSwitcher() {
      this.refreshAccounts()
      this.isAccountSwitcherOpen = true
    },
    closeAccountSwitcher() {
      this.isAccountSwitcherOpen = false
    },
    goToAddAccount() {
      this.isAccountSwitcherOpen = false
      this.$router.push({path: '/login', query: {mode: 'add'}})
    },
    refreshAccounts() {
      const snapshot = getAccountsSnapshot()
      this.accounts = snapshot.accounts || []
      this.activeAccountId = snapshot.activeId
    },
    resolveAccountName(accountItem) {
      if (!accountItem) {
        return 'Аккаунт'
      }
      const name = accountItem.userName || accountItem.login
      return name && String(name).trim() !== '' ? name : 'Аккаунт'
    },
    resolveAgreementNumber(accountItem) {
      if (!accountItem) {
        return '—'
      }
      const number = accountItem.agreementNumber
      return number && String(number).trim() !== '' ? number : '—'
    },
    resolvePersonalAccount(accountItem) {
      if (!accountItem) {
        return ''
      }
      const value = accountItem.personalAccount
      return value && String(value).trim() !== '' ? value : ''
    },
    switchToAccount(accountId) {
      if (!accountId || accountId === this.activeAccountId) {
        return
      }
      const result = switchAccount(accountId)
      if (result && result.ok) {
        window.location.reload()
      }
    },
    removeAccountEntry(accountId) {
      if (!accountId) {
        return
      }
      const accountItem = this.accounts.find(item => item.id === accountId)
      const accountName = accountItem ? this.resolveAccountName(accountItem) : 'аккаунта'
      const confirmed = window.confirm(`Выйти из ${accountName}?`)
      if (!confirmed) {
        return
      }
      const result = removeAccount(accountId)
      if (result && result.hasAccounts) {
        window.location.reload()
        return
      }
      this.isAccountSwitcherOpen = false
      handleAuthFailure({router: this.$router, reloadOnSwitch: false})
    },
    performLogout() {
      handleAuthFailure({router: this.$router, reloadOnSwitch: true})
    }
  },
  mounted() {
    this.refreshAccounts()
    window.addEventListener('accounts:updated', this.refreshAccounts)
  },
  beforeUnmount() {
    window.removeEventListener('accounts:updated', this.refreshAccounts)
  }
}
</script>

<style scoped>
.page-scaffold {
  background: #FAFDFF;
  position: absolute;
  top: 0;
  left: 0;
  overflow-y: scroll;
  height: 100vh;
  width: 100vw;
  display: grid;
  grid-template-columns: 1fr minmax(auto, 1400px) 1fr;
  grid-template-rows: max-content 1fr;
  grid-template-areas:
      "safe-zone-l top-bar safe-zone-r"
      "safe-zone-l content safe-zone-r";
}

.page-scaffold .safe-zone {
}

.page-scaffold .content {
}

.account-switcher {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
}

.account-switcher__overlay {
  position: absolute;
  inset: 0;
  background: rgba(8, 24, 32, 0.45);
}

.account-switcher__panel {
  position: relative;
  width: min(640px, 92vw);
  max-height: 80vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 24px 60px rgba(13, 38, 56, 0.25);
  z-index: 1;
}

.account-switcher__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.account-switcher__title {
  font-family: "Mont", serif;
  font-weight: 700;
  color: #2D666E;
  font-size: 1.4rem;
}

.account-switcher__close {
  border: none;
  background: transparent;
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
  color: #2d666e;
}

.account-switcher__actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-start;
}

.account-switcher__add {
  border: none;
  border-radius: 12px;
  padding: 10px 16px;
  background: #58B1DA;
  color: white;
  font-family: "Mont", serif;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease;
}

.account-switcher__add:hover,
.account-switcher__add:focus-visible {
  background: #2d94c1;
}

.account-switcher__list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.account-switcher__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #f7fafb;
  border: 1px solid #e0e7ee;
}

.account-switcher__item--active {
  border-color: #58B1DA;
  background: #eef7fb;
  box-shadow: 0 0 0 2px rgba(88, 177, 218, 0.15);
}

.account-switcher__info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
}

.account-switcher__name {
  font-family: "Mont", serif;
  font-weight: 700;
  color: #24484e;
}

.account-switcher__meta {
  font-family: "Mont", serif;
  font-size: 0.9rem;
  color: #6a8b9b;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.account-switcher__controls {
  display: flex;
  gap: 8px;
}

.account-switcher__btn {
  border: none;
  border-radius: 10px;
  padding: 8px 12px;
  font-family: "Mont", serif;
  font-weight: 600;
  cursor: pointer;
  background: #0082bf;
  color: #ffffff;
  transition: opacity 0.2s ease, background 0.2s ease;
}

.account-switcher__btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.account-switcher__btn--ghost {
  background: transparent;
  color: #0082bf;
  border: 1px solid rgba(0, 130, 191, 0.4);
}

.account-switcher__empty {
  margin-top: 12px;
  font-family: "Mont", serif;
  color: #6a8b9b;
}

@media (max-width: 900px) {
  .account-switcher__item {
    flex-direction: column;
    align-items: flex-start;
  }

  .account-switcher__controls {
    width: 100%;
  }

  .account-switcher__btn {
    flex: 1;
    text-align: center;
  }
}
</style>
